import React from "react";
import InputKMQ from "../../UI/Input/InputKMQ";
import SelectKMQ from "../../UI/Select/SelectKMQ";
import Checkbox from "../../UI/Checkbox/Checkbox";
import CheckIcon from "../../../assets/icons/UI/icons/checkmark.svg";
import ResumeDates from "../CplApplicantProfile/components/ResumeDates/ResumeDates";
import { WarningCircle } from "@phosphor-icons/react";
import ErrorKMQ from "../../UI/Error/ErrorKMQ";
import FileUpload from "../CplApplicantProfile/components/FileUpload";

interface SubmittedEducationSectionProps {
  educations: any[];
  editMode: boolean;
  locationOptions: { label: string; value: string }[];
  institutionTypeOptions: { label: string; value: string }[];
  currOptions: any;
  isRecommendationError: boolean;
  updateEducation: (index: number, field: string, value: any) => void;
  handleSelectEducationNormalization: (eduIndex: number, value: string) => void;
  handleResumeEducationDateChange: (newDateInfo: any, index: number) => void;
  mapApiDocumentsToAttachments: (documents: any[]) => any[];
  getDegreeTypeOptions: (institutionType: string) => { label: string; value: string }[];
}

const SubmittedEducationSection: React.FC<SubmittedEducationSectionProps> = ({
  educations,
  editMode,
  locationOptions,
  institutionTypeOptions,
  currOptions,
  isRecommendationError,
  updateEducation,
  handleSelectEducationNormalization,
  handleResumeEducationDateChange,
  mapApiDocumentsToAttachments,
  getDegreeTypeOptions
}) => {
  const renderNormalizationOptions = (
    normalizationChoice: string,
    description: string
  ) => {
    return (
      <div className="normalization-option">
        <div className="normalization-option-title">
          <img
            src={CheckIcon}
            alt={"check-icon"}
            className={"normalization-option-title-active"}
          />
          {normalizationChoice}
        </div>
        {description && (
          <div className={"normalization-option-text"}>{description}</div>
        )}
      </div>
    );
  };

  const handleDegreeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (editMode) {
      updateEducation(index, 'program', e.target.value);
    }
  };

  const handleDegreePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    if (editMode) {
      requestAnimationFrame(() => {
        const inputElements = document.querySelectorAll('input[aria-label="Degree program"]');
        if (inputElements && inputElements[index]) {
          const value = (inputElements[index] as HTMLInputElement).value;
          updateEducation(index, 'program', value);
        }
      });
    }
  };

  const handleDegreeBlur = (e: React.FocusEvent<HTMLInputElement>, index: number) => {
    if (editMode) {
      updateEducation(index, 'program', e.target.value);
    }
  };

  const handleInstitutionChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (editMode) {
      updateEducation(index, 'institution_name', e.target.value);
    }
  };

  const handleInstitutionPaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    if (editMode) {
      requestAnimationFrame(() => {
        const inputElements = document.querySelectorAll('input[aria-label="Name of Institution"]');
        if (inputElements && inputElements[index]) {
          const value = (inputElements[index] as HTMLInputElement).value;
          updateEducation(index, 'institution_name', value);
        }
      });
    }
  };

  const handleInstitutionBlur = (e: React.FocusEvent<HTMLInputElement>, index: number) => {
    if (editMode) {
      updateEducation(index, 'institution_name', e.target.value);
    }
  };

  return (
    <div>
      <div className="edit-resume-education-title">Education History</div>
      {educations?.map((education: any, index: number) => (
        <div key={index} className="work-experience">
          <h4>Degree Program {index + 1}</h4>
          <InputKMQ
            value={education.institution_name ?? ""}
            label={"Name of Institution"}
            onChange={(e) => handleInstitutionChange(e, index)}
            onPaste={(e) => handleInstitutionPaste(e, index)}
            onBlur={(e) => handleInstitutionBlur(e, index)}
            type="text"
            placeholder=""
          />
          <SelectKMQ
            options={locationOptions}
            value={
              education.location
                ? { label: education.location, value: education.location }
                : null
            }
            label={"Location"}
            onChange={(option) => editMode ? updateEducation(index, 'location', option.value) : null}
          />
          <SelectKMQ
            options={institutionTypeOptions}
            value={
              education.institution_type
                ? {
                  label: education.institution_type,
                  value: education.institution_type,
                }
                : null
            }
            label={"Type of institution"}
            onChange={(option) => editMode ? updateEducation(index, 'institution_type', option.value) : null}
          />
          {education.institution_type ? (
            <SelectKMQ
              options={getDegreeTypeOptions(education.institution_type)}
              value={
                education.degree_type
                  ? { label: education.degree_type, value: education.degree_type }
                  : null
              }
              label={"Type of degree"}
              onChange={(option) => editMode ? updateEducation(index, 'degree_type', option.value) : null}
            />
          ) : null}
          <InputKMQ
            value={education.program ?? ""}
            label={"Degree program"}
            onChange={(e) => handleDegreeChange(e, index)}
            onPaste={(e) => handleDegreePaste(e, index)}
            onBlur={(e) => handleDegreeBlur(e, index)}
            type="text"
            placeholder=""
          />

          {
            // @ts-ignore
            editMode && education.program && education.institution_name && currOptions.educations && currOptions.educations[index]?.normalization_choices?.length > 0 ? (
              <div>
                <SelectKMQ
                  // @ts-ignore
                  options={currOptions.educations[index].normalization_choices.map((item: { title: string }) => ({
                    label: item.title,
                    value: item.title,
                  }))}
                  value={education.normalized ? { label: education.normalized, value: education.normalized } : 
                        education.normalization_choice ? { label: education.normalization_choice, value: education.normalization_choice } : null}
                  onChange={(option) => handleSelectEducationNormalization(index, option.value)}
                  label={"Selected degree program"}
                  placeholder={"Choose your degree program"}
                  error={isRecommendationError && !education.normalized}
                />
                {isRecommendationError && !education.normalized ? (
                  <div className="education-error">
                    <ErrorKMQ error={"Some fields are missing or incomplete, please fill in all required information."} />
                  </div>
                ) : null}
                <div className={"education-notification"}>
                  <WarningCircle size={21} color={"#212121"} />
                  Please select the degree program that closely matches the degree program title.
                </div>

                <FileUpload
                  onSave={(value) => {
                    if (value && value.related_documents) {
                      updateEducation(index, 'related_documents', value.related_documents);
                    }
                  }}
                  // @ts-ignore
                  files={mapApiDocumentsToAttachments(education.related_documents)}
                  optional
                  maxFiles={5}
                />
              </div>
            ) : education.normalized && !editMode ? (
              renderNormalizationOptions(
                education.normalized,
                education.normalization_choice_descriptions || "Selected degree program"
              )
            ) : null}

          <Checkbox
            active={!education.currently_enrolled}
            text={"I have graduated"}
            handleClick={() => editMode ? updateEducation(index, 'currently_enrolled', !education.currently_enrolled) : null}
            disabled={!editMode}
          />
          <ResumeDates
            start_date={education.start_date}
            end_date={education.end_date}
            hideFutureDates={education.currently_enrolled ?? false}
            changeResume={(newDateInfo) => handleResumeEducationDateChange(newDateInfo, index)}
            active={education.currently_enrolled}
          />
        </div>
      ))}
    </div>
  );
};

export default SubmittedEducationSection;